import { useState, useEffect } from 'react';
import { CreditCard, Calendar, CheckCircle, XCircle, Clock, Download, TrendingUp, Package } from 'lucide-react';
import PlanService from '@services/api/plan.service';
import { toast } from 'react-hot-toast';

const PaymentHistory = () => {
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [filter, setFilter] = useState('all'); // all, success, failed, pending

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await PlanService.getPaymentHistory();
      if (response.success) {
        setPaymentData(response.data);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Success' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      created: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Created' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const getTransactionTypeBadge = (type) => {
    const typeConfig = {
      new_subscription: { color: 'bg-blue-50 text-blue-700', label: 'New Subscription' },
      upgrade: { color: 'bg-purple-50 text-purple-700', label: 'Upgrade' },
      downgrade: { color: 'bg-orange-50 text-orange-700', label: 'Downgrade' },
      renewal: { color: 'bg-green-50 text-green-700', label: 'Renewal' }
    };

    const config = typeConfig[type] || { color: 'bg-gray-50 text-gray-700', label: type };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, dateString);
      return 'N/A';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, dateString);
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredTransactions = paymentData?.transactions?.filter(transaction => {
    if (filter === 'all') return true;
    return transaction.status === filter;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-2 sm:py-3 px-2 sm:px-3">
      <div className="w-full max-w-[1600px] mx-auto">

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-2 sm:mb-3">
          <div className="bg-white rounded-lg shadow-sm p-2 sm:p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-0.5">Total Spent</p>
                <p className="text-base sm:text-xl font-bold text-gray-900">
                  {formatCurrency(paymentData?.summary?.totalSpent || 0)}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-2 sm:p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-0.5">Total Transactions</p>
                <p className="text-base sm:text-xl font-bold text-gray-900">
                  {paymentData?.summary?.totalTransactions || 0}
                </p>
              </div>
              <div className="bg-purple-100 p-2 rounded-lg">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-2 sm:p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-0.5">Successful</p>
                <p className="text-base sm:text-xl font-bold text-green-600">
                  {paymentData?.summary?.successfulPayments || 0}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-2 sm:p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-0.5">Failed</p>
                <p className="text-base sm:text-xl font-bold text-red-600">
                  {paymentData?.summary?.failedPayments || 0}
                </p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Current Subscription */}
        {paymentData?.currentSubscription && (
          <div className="bg-gradient-to-r from-[#1a2642] to-[#243556] rounded-lg shadow-lg p-3 mb-2 sm:mb-3 text-white border border-[#4a5f7f]">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Package className="w-5 h-5 sm:w-6 sm:h-6" />
                  <h2 className="text-lg sm:text-xl font-bold">Current Plan</h2>
                </div>
                <p className="text-xl sm:text-2xl font-bold mb-2">{paymentData.currentSubscription.planName}</p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Started: {formatDate(paymentData.currentSubscription.startedAt)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Expires: {formatDate(paymentData.currentSubscription.expiresAt)}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  paymentData.currentSubscription.status === 'active' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-yellow-500 text-white'
                }`}>
                  {paymentData.currentSubscription.status.toUpperCase()}
                </span>
                {/* <div className="mt-4 text-sm">
                  <p>Portfolios: {paymentData.currentSubscription.portfoliosCreated}</p>
                  <p>Career Maps: {paymentData.currentSubscription.careerMapsGenerated}</p>
                  <p>Credits Used: {paymentData.currentSubscription.creditsUsed}</p>
                </div> */}
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-2 sm:mb-3">
          <div className="flex border-b border-gray-200">
            {['all', 'success', 'failed', 'pending'].map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`flex-1 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                  filter === filterOption
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No transactions found</p>
              <p className="text-gray-400 text-sm mt-2">Your payment history will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto w-full" style={{maxHeight: 'calc(100vh - 280px)', minHeight: '300px'}}>
              <table className="w-full divide-y divide-gray-200 min-w-max">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Transaction ID
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Plan
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Type
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Amount
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Status
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Date
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <CreditCard className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm font-mono text-gray-900">
                            {transaction.transactionId?.substring(0, 20)}...
                          </span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{transaction.planName}</div>
                        {transaction.metadata?.mentorCredits > 0 && (
                          <div className="text-xs text-green-600 font-medium">
                            +{transaction.metadata.mentorCredits} Mentor Credits
                          </div>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        {getTransactionTypeBadge(transaction.transactionType)}
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(transaction.amount)}
                        </div>
                        <div className="text-xs text-gray-500">{transaction.currency}</div>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        {getStatusBadge(transaction.status)}
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          {formatDateTime(transaction.createdAt)}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-sm">
                        <div className="space-y-1">
                          {transaction.subscription && transaction.subscription.expiresAt && (
                            <div className="text-xs text-gray-600">
                              Valid till: {formatDate(transaction.subscription.expiresAt)}
                            </div>
                          )}
                          {transaction.metadata?.billingCycle && (
                            <div className="text-xs text-gray-500 capitalize">
                              {transaction.metadata.billingCycle}
                            </div>
                          )}
                          {transaction.metadata?.simulatedPayment && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              Direct Activation
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Subscription History */}
        {paymentData?.subscriptionHistory && paymentData.subscriptionHistory.length > 0 && (
          <div className="mt-2 sm:mt-3 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Subscription Changes</h2>
            <div className="space-y-3 sm:space-y-4 max-h-64 sm:max-h-96 overflow-y-auto pr-2">
              {paymentData.subscriptionHistory.map((history) => (
                <div key={history.id} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Package className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900 capitalize">{history.action}</span>
                      <span className="text-xs text-gray-500">{formatDate(history.createdAt)}</span>
                    </div>
                    {history.fromPlan && history.toPlan && (
                      <p className="text-sm text-gray-600">
                        Changed from <span className="font-medium">{history.fromPlan.name}</span> ({formatCurrency(history.fromPlan.price)})
                        to <span className="font-medium">{history.toPlan.name}</span> ({formatCurrency(history.toPlan.price)})
                      </p>
                    )}
                    {!history.fromPlan && history.toPlan && (
                      <p className="text-sm text-gray-600">
                        Activated <span className="font-medium">{history.toPlan.name}</span> plan ({formatCurrency(history.toPlan.price)})
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
